<template>
  <b-card title="Podcast">
    <div class="m-2">
      <b-row>
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <label>Show</label>
          <v-select
            v-model="perPage"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :options="perPageOptions"
            :clearable="false"
            class="per-page-selector d-inline-block ml-50 mr-1"
          />
          <b-button variant="primary" :to="{ name: 'admin-podcast-add' }">
            Create Podcast
          </b-button>
        </b-col>
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-end mb-1 mb-md-0"
        >
          <label class="mr-1">
            Search
          </label>
          <div class="d-flex align-items-center justify-content-end">
            <b-form-input
              class="d-inline-block mr-1"
              placeholder="Search..."
              v-model="searchWord"
            />
          </div>
        </b-col>
      </b-row>
    </div>
    <div class="d-none d-md-flex">
      <b-table responsive :items="podcast" :fields="fields" :busy="isBusy" class="w-100">
        <template #table-busy>
          <div class="text-center text-danger my-2">
            <b-spinner class="align-middle mr-1" variant="secondary"></b-spinner>
            <strong class="text-secondary">Loading...</strong>
          </div>
        </template>
        <template #cell(action)="podcast">
          <div class="d-flex justify-content-start">
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="secondary"
              class="btn-icon mr-1"
              :to="{ name: 'admin-podcast-edit', params: { id: podcast.item.id } }"
            >
              <feather-icon icon="Edit3Icon" />
            </b-button>
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="danger"
              class="btn-icon"
              @click="confirmDeletePodcast(podcast.item.id)"
            >
              <feather-icon icon="Trash2Icon" />
            </b-button>
          </div>
        </template>
        <template #cell(description)="podcast">
          <div :inner-html.prop="podcast.item.description | truncate(250)" style="min-width: 300px"></div>
        </template>
        <template #cell(createdat)="podcast">
          <div>
            {{
              podcast.item.createdat
                ? $moment(podcast.item.createdat).format('dddd, Do MMMM YYYY')
                : '-'
            }}
          </div>
        </template>
      </b-table>
    </div>
    <!-- Mobile screen -->
    <div class="d-flex d-md-none flex-column">
      <!-- Loading -->
      <div v-if="isBusy" class="text-center text-danger my-2">
        <b-spinner class="align-middle mr-1" variant="secondary"></b-spinner>
        <strong class="text-secondary">Loading...</strong>
      </div>
      <!-- Data -->
      <b-row
        v-if="!isBusy"
        v-for="(item, index) in podcast"
        :key="item.id"
        class="mb-3 list-item"
      >
        <b-col cols="12" class="d-flex flex-wrap justify-content-end align-items-center mb-1" style="gap: 1rem;">
          <strong class="flex-grow-1 truncate">{{ item.title }}</strong>
          <div>
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="secondary"
              class="btn-icon mr-1"
              :to="{ name: 'admin-podcast-edit', params: { id: item.id } }"
            >
              <feather-icon icon="Edit3Icon" />
            </b-button>
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="danger"
              class="btn-icon"
              @click="confirmDeletePodcast(item.id)"
            >
              <feather-icon icon="Trash2Icon" />
            </b-button>
          </div>
        </b-col>
        <b-col cols="12" class="d-flex flex-wrap">
          <div style="font-size: 0.75rem;">
            <p style="word-break: break-all;">
              <strong>YOUTUBE LINK:</strong> {{ item.youtube_link }}
            </p>
            <p><strong>LABEL:</strong> {{ item.label }}</p>
            <p>
              <strong>DESCRIPTION:</strong> 
              <span :inner-html.prop="item.description | truncate(250)"></span>
            </p>
            <p><strong>CREATED AT:</strong> {{ item.createdat ? $moment(item.createdat).format('dddd, Do MMMM YYYY') : '-' }}</p>
          </div>
        </b-col>
      </b-row>
    </div>
    <div class="mx-2 mb-2">
      <b-row>
        <b-col
          cols="12"
          sm="6"
          class="d-flex align-items-center justify-content-center justify-content-sm-start"
        >
          <span class="text-muted">
            Showing {{ pagination.from }} to {{ pagination.to }} of
            {{ pagination.total }} entries
          </span>
        </b-col>
        <!-- Pagination -->
        <b-col
          cols="12"
          sm="6"
          class="d-flex align-items-center justify-content-center justify-content-sm-end"
        >
          <b-pagination
            v-model="currentPage"
            :total-rows="pagination.total"
            :per-page="perPage"
            first-number
            last-number
            class="mb-0 mt-1 mt-sm-0"
            prev-class="prev-item"
            next-class="next-item"
            @input="changePage"
          >
            <template #prev-text>
              <feather-icon icon="ChevronLeftIcon" size="18" />
            </template>
            <template #next-text>
              <feather-icon icon="ChevronRightIcon" size="18" />
            </template>
          </b-pagination>
        </b-col>
      </b-row>
    </div>
  </b-card>
</template>

<script>
import {
  BCard,
  BCardText,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BTable,
  BMedia,
  BAvatar,
  BLink,
  BBadge,
  BDropdown,
  BDropdownItem,
  BPagination,
  BTooltip,
  BSpinner,
  BFormCheckbox,
  BModal,
  BImg,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import Ripple from 'vue-ripple-directive'
import { generateToken } from '@/auth/helperAuth'

export default {
  components: {
    BCard,
    BCardText,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BMedia,
    BAvatar,
    BLink,
    BBadge,
    BDropdown,
    BDropdownItem,
    BPagination,
    BTooltip,
    BSpinner,
    BFormCheckbox,
    BModal,
    BImg,

    vSelect,
  },
  data() {
    return {
      token: '',
      isBusy: false,
      currentPage: 1,
      skip:0,
      limit:1000,
      perPage: 10,
      perPageOptions: [10, 20, 30],
      podcastRaw: [], 
      pagination: {
        from: 1,
        to: 10,
        total: 0,
      },
      fields: [
        { key: 'title', sortable: true },
        'youtube_link',
        'label',
        'description',
        'createdat',
        'action',
      ],
      podcast: [],
      searchWord: '',
    }
  },

  directives: {
    Ripple,
  },

  created() {
    const payload = {
      client: "product-catalog-fe",
      kode: this.$store.getters['oauth/getActiveUser'].id.toString(),
      user: this.$store.getters['oauth/getActiveUser'].name,
      role: this.$store.getters['oauth/getActiveUser'].role,
    };
    this.token = generateToken(payload)
  },

  watch: {
    perPage() {
      this.currentPage = 1;
      this.paginatePodcast();
    },
    currentPage() {
      this.paginatePodcast();
    },
    searchWord() {
      this.fetchNews()
    },
  },

  filters: {
    truncate: function(value, length, start = 0) {
      if (!value) return ''
      value = value.toString()
      if (value.length > length) {
        if (start > 0) {
          return value.substring(start, length + start) + '...'
        } else {
          return value.substring(start, length) + '...'
        }
      } else {
        return value
      }
    },
  },

  mounted() {
    this.fetchNews()
  },

  methods: {
    fetchNews() {
    this.isBusy = true;

    const skip = (this.currentPage - 1) * this.perPage;

    this.$http
      .get(`${process.env.VUE_APP_API_HELPER_URL}/podcast`, {
        params: {
          skip: skip,
          limit: this.limit,
        },
        headers: {
          'Authorization': this.token,
          'accept': 'application/json',
        },
      })
      .then(({ data }) => {
        const allData = (data.data || []).sort((a, b) => new Date(b.createdat) - new Date(a.createdat));
        this.podcastRaw = allData; 
        this.pagination.total = allData.length;
        this.paginatePodcast();
      })
      .finally(() => {
        this.isBusy = false;
      });
    },
    changePage(page) {
      this.currentPage = page
    },
    paginatePodcast() {
      const start = (this.currentPage - 1) * this.perPage;
      const end = start + this.perPage;
      this.podcast = this.podcastRaw.slice(start, end); 
      this.pagination.from = start + 1;
      this.pagination.to = Math.min(end, this.podcastRaw.length); 
    },

   async deletePodcast(podcastId) {
    this.isBusy = true
    return await this.$http
      .delete(`${process.env.VUE_APP_API_HELPER_URL}/internal/data`, {
        params: {
          table: 'podcast',
          id: podcastId,
        },
        headers: {
          Authorization: this.token,
          accept: 'application/json',
        },
      })
      .then(() => {
        this.$notif('Success', 'Podcast has been successfully deleted.', 'success')
      })
      .catch(({ data }) => {
        this.$notif('Something Error', data?.message || 'Delete failed.', 'danger')
      })
      .finally(() => {
        this.isBusy = false
      })
    },

    confirmDeletePodcast(podcastId) {
      this.$bvModal
        .msgBoxConfirm('Please confirm that you want to delete this item.', {
          title: 'Please Confirm',
          size: 'sm',
          okVariant: 'danger',
          okTitle: 'Yes',
          cancelTitle: 'No',
          cancelVariant: 'outline-secondary',
          hideHeaderClose: false,
          centered: true,
        })
        .then(value => {
          if (value === true) {
            this.deletePodcast(podcastId).then(() => {
              this.fetchNews()
            })
          }
        })
    },

  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
